<template>
  <b-card header-tag="header" footer-tag="footer">
    <template v-slot:header>
      <h6 class="mb-0" v-if="showName">Valve Configuration ({{ getSafe(() => device.object) }})</h6>
      <h6 class="mb-0" v-else>Valve Configuration</h6>
    </template>

    <template v-if="loading">
      <div class="d-flex justify-content-center mb-3">
        <b-spinner variant="primary"></b-spinner>
      </div>
    </template>

    <div v-else>
      <!-- ============ VALVE VISUAL ============ -->
      <div class="valve-visual-wrap">
        <svg viewBox="0 0 260 150" class="valve-svg">
          <!-- pipes -->
          <rect x="0" y="100" width="70" height="30" rx="3" class="v-pipe" />
          <rect x="190" y="100" width="70" height="30" rx="3" class="v-pipe" />
          <!-- pipe flanges -->
          <rect x="62" y="94" width="10" height="42" rx="2" class="v-flange" />
          <rect x="188" y="94" width="10" height="42" rx="2" class="v-flange" />
          <!-- water flow (only when open) -->
          <g v-if="flowVisible">
            <circle v-for="n in 3" :key="'d'+n" r="3" class="v-drop">
              <animate
                attributeName="cx"
                :begin="(n * 0.4) + 's'"
                from="10"
                to="250"
                dur="1.4s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="cy"
                :values="'115;112;118;115'"
                dur="0.7s"
                repeatCount="indefinite"
              />
            </circle>
          </g>
          <!-- valve body -->
          <path d="M95 95 L165 95 L172 135 L88 135 Z" class="v-body" />
          <!-- bonnet / stem -->
          <rect x="124" y="60" width="12" height="38" class="v-stem" />
          <!-- handwheel (rotates while jogging) -->
          <g class="v-wheel" :class="{ 'v-wheel-spin-cw': spinDir === 1, 'v-wheel-spin-ccw': spinDir === -1 }">
            <circle cx="130" cy="45" r="30" class="v-wheel-rim" />
            <circle cx="130" cy="45" r="5" class="v-wheel-hub" />
            <line x1="130" y1="17" x2="130" y2="73" class="v-spoke" />
            <line x1="102" y1="45" x2="158" y2="45" class="v-spoke" />
            <line x1="110" y1="25" x2="150" y2="65" class="v-spoke" />
            <line x1="150" y1="25" x2="110" y2="65" class="v-spoke" />
          </g>
          <!-- position readout -->
          <text x="130" y="148" text-anchor="middle" class="v-degrees">{{ estDegrees }}&deg;</text>
        </svg>

        <div class="text-center">
          <b-badge :variant="stateBadgeVariant" class="mr-1">{{ stateBadgeText }}</b-badge>
          <b-badge variant="light">position: {{ estPosition }} steps</b-badge>
        </div>
      </div>

      <hr class="my-3" />

      <!-- ============ IDLE / SUMMARY ============ -->
      <div v-if="wizardStep === 0">
        <p class="text-muted mb-2">
          Calibration teaches the valve its <strong>CLOSED</strong> and <strong>OPEN</strong>
          positions. The motor moves in real time as you press the controls.
        </p>
        <div class="d-flex justify-content-between align-items-center">
          <b-button variant="outline-secondary" size="sm" @click="requestCalInfo" :disabled="sending">
            <b-spinner small v-show="sendingInfo"></b-spinner>
            Query device (cal_info)
          </b-button>
          <b-button variant="primary" @click="startWizard" :disabled="sending">
            Start calibration
          </b-button>
        </div>
      </div>

      <!-- ============ WIZARD ============ -->
      <div v-else>
        <!-- progress -->
        <div class="d-flex align-items-center mb-3">
          <div
            v-for="s in 3"
            :key="'step'+s"
            class="wizard-dot"
            :class="{ 'wizard-dot-active': wizardStep === s, 'wizard-dot-done': wizardStep > s }"
          >{{ s }}</div>
          <span class="ml-2 font-weight-bold">{{ stepTitle }}</span>
        </div>

        <!-- STEP 1: close valve -->
        <div v-if="wizardStep === 1">
          <p class="text-muted">
            Use the jog buttons until the valve is <strong>fully closed</strong> (no water flows),
            then mark this position as ZERO.
          </p>
        </div>

        <!-- STEP 2: open valve -->
        <div v-if="wizardStep === 2">
          <p class="text-muted">
            Now jog the valve until it is <strong>fully open</strong> (good water flow),
            then save this as the OPEN position.
          </p>
          <b-alert show variant="warning" v-if="estPosition === 0" class="py-1 px-2">
            Move the valve first — the OPEN position cannot be the same as ZERO.
          </b-alert>
        </div>

        <!-- STEP 3: done -->
        <div v-if="wizardStep === 3">
          <b-alert show variant="success">
            <strong>Calibration complete.</strong>
            OPEN position saved at {{ savedOpenPosition }} steps ({{ stepsToDegrees(savedOpenPosition) }}&deg;).
            The valve will now travel between these two points.
          </b-alert>
          <div class="d-flex justify-content-end">
            <b-button variant="primary" @click="wizardStep = 0">Finish</b-button>
          </div>
        </div>

        <!-- jog controls (steps 1 & 2) -->
        <div v-if="wizardStep === 1 || wizardStep === 2">
          <b-form-group label="Movement size:" class="mb-2">
            <b-form-radio-group
              v-model="jogSize"
              :options="jogSizeOptions"
              buttons
              button-variant="outline-secondary"
              size="sm"
            ></b-form-radio-group>
          </b-form-group>

          <div class="d-flex justify-content-center my-3">
            <b-button
              variant="outline-primary"
              class="mx-2 jog-btn"
              @click="jog(-1)"
              :disabled="sending"
            >
              &#8634; Close direction
            </b-button>
            <b-button
              variant="outline-primary"
              class="mx-2 jog-btn"
              @click="jog(1)"
              :disabled="sending"
            >
              &#8635; Open direction
            </b-button>
          </div>

          <div class="d-flex justify-content-between mt-3">
            <b-button variant="light" @click="cancelWizard" :disabled="sending">Cancel</b-button>
            <b-button
              v-if="wizardStep === 1"
              variant="danger"
              @click="confirmZero"
              :disabled="sending"
            >
              <b-spinner small v-show="sendingAction"></b-spinner>
              Mark as CLOSED (zero)
            </b-button>
            <b-button
              v-if="wizardStep === 2"
              variant="success"
              @click="confirmActive"
              :disabled="sending || estPosition === 0"
            >
              <b-spinner small v-show="sendingAction"></b-spinner>
              Save as OPEN
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import { BROADCAST_ACTION } from "@/core/services/store/devices.module";

// 28BYJ-48: 4096 half-steps per revolution
const STEPS_PER_REV = 4096;

export default {
  name: "suitch-valve-config-widget",
  props: {
    device: {
      type: Object,
      required: true,
    },
    showName: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      wizardStep: 0, // 0 = idle, 1 = set zero, 2 = set open, 3 = done
      sendingJog: false,
      sendingAction: false,
      sendingInfo: false,
      // optimistic position estimate in half-steps (device responses are not
      // surfaced to the web yet; they travel to the IoT server only)
      estPosition: 0,
      savedOpenPosition: 1024, // firmware default (90 degrees)
      spinDir: 0, // 1 = clockwise, -1 = counter-clockwise, 0 = still
      spinTimer: null,
      jogSize: 50,
      jogSizeOptions: [
        { text: "Fine (10)", value: 10 },
        { text: "Normal (50)", value: 50 },
        { text: "Coarse (200)", value: 200 },
      ],
    };
  },
  computed: {
    sending() {
      return this.sendingJog || this.sendingAction || this.sendingInfo;
    },
    estDegrees() {
      return this.stepsToDegrees(this.estPosition);
    },
    flowVisible() {
      // show water flowing when the valve is meaningfully open
      return this.estPosition > 100;
    },
    stateBadgeVariant() {
      if (this.spinDir !== 0) return "warning";
      return this.estPosition === 0 ? "secondary" : "success";
    },
    stateBadgeText() {
      if (this.spinDir !== 0) return "MOVING...";
      return this.estPosition === 0 ? "CLOSED" : "OPEN";
    },
    stepTitle() {
      switch (this.wizardStep) {
        case 1: return "Step 1 — Find the CLOSED position";
        case 2: return "Step 2 — Find the OPEN position";
        case 3: return "Step 3 — Done";
        default: return "";
      }
    },
  },
  mounted() {
    setTimeout(() => {
      this.loading = false;
    }, 600);
  },
  beforeDestroy() {
    if (this.spinTimer) clearTimeout(this.spinTimer);
  },
  methods: {
    getSafe(fn) {
      try {
        return fn();
      } catch (e) {
        return "";
      }
    },
    stepsToDegrees(steps) {
      return Math.round((steps * 360) / STEPS_PER_REV);
    },
    startWizard() {
      this.wizardStep = 1;
    },
    cancelWizard() {
      this.wizardStep = 0;
    },
    jog(direction) {
      const command = (direction > 0 ? "move+" : "move-") + ":" + this.jogSize;
      this.sendingJog = true;
      this.sendCommand(command)
        .then(() => {
          this.estPosition += direction * this.jogSize;
          this.animateWheel(direction);
        })
        .finally(() => {
          this.sendingJog = false;
        });
    },
    confirmZero() {
      this.sendingAction = true;
      this.sendCommand("set_zero")
        .then(() => {
          this.estPosition = 0;
          this.$bvToast.toast("Current position saved as CLOSED (zero)", {
            title: "Calibration",
            variant: "success",
            solid: true,
          });
          this.wizardStep = 2;
        })
        .finally(() => {
          this.sendingAction = false;
        });
    },
    confirmActive() {
      this.sendingAction = true;
      this.sendCommand("set_active")
        .then(() => {
          this.savedOpenPosition = this.estPosition;
          this.$bvToast.toast("Current position saved as OPEN", {
            title: "Calibration",
            variant: "success",
            solid: true,
          });
          this.wizardStep = 3;
        })
        .finally(() => {
          this.sendingAction = false;
        });
    },
    requestCalInfo() {
      this.sendingInfo = true;
      this.sendCommand("cal_info")
        .then(() => {
          this.$bvToast.toast(
            "cal_info requested — the device reports its position to the IoT server",
            { title: "Calibration", variant: "info", solid: true }
          );
        })
        .finally(() => {
          this.sendingInfo = false;
        });
    },
    animateWheel(direction) {
      // spin roughly as long as the motor travels (300 half-steps/s in firmware)
      this.spinDir = direction;
      if (this.spinTimer) clearTimeout(this.spinTimer);
      const durationMs = Math.max(500, (this.jogSize / 300) * 1000);
      this.spinTimer = setTimeout(() => {
        this.spinDir = 0;
      }, durationMs);
    },
    sendCommand(commandData) {
      return this.$store
        .dispatch(BROADCAST_ACTION, {
          token: this.device.token,
          secret: this.device.secret_token,
          command_data: commandData,
        })
        .catch((error) => {
          console.error("Error sending valve command:", error);
          this.$bvToast.toast(`Failed to send "${commandData}" to the valve`, {
            title: "Error",
            variant: "danger",
            solid: true,
          });
          throw error;
        });
    },
  },
};
</script>

<style scoped>
.card-header {
  background-color: #f8f9fa;
}

/* ---- valve drawing ---- */
.valve-visual-wrap {
  max-width: 320px;
  margin: 0 auto;
}
.valve-svg {
  width: 100%;
  display: block;
}
.v-pipe {
  fill: #b8c2cc;
  stroke: #8a959f;
  stroke-width: 1.5;
}
.v-flange {
  fill: #99a4ae;
  stroke: #7a858f;
  stroke-width: 1;
}
.v-body {
  fill: #6c7a89;
  stroke: #56616d;
  stroke-width: 1.5;
}
.v-stem {
  fill: #99a4ae;
  stroke: #7a858f;
  stroke-width: 1;
}
.v-wheel-rim {
  fill: none;
  stroke: #e64c3c;
  stroke-width: 7;
}
.v-wheel-hub {
  fill: #c0392b;
}
.v-spoke {
  stroke: #e64c3c;
  stroke-width: 4;
  stroke-linecap: round;
}
.v-drop {
  fill: #3699ff;
  opacity: 0.85;
}
.v-degrees {
  font-size: 12px;
  font-weight: 600;
  fill: #6c7a89;
}

/* handwheel rotation while jogging */
.v-wheel {
  transform-origin: 130px 45px;
}
.v-wheel-spin-cw {
  animation: valve-spin-cw 1.2s linear infinite;
}
.v-wheel-spin-ccw {
  animation: valve-spin-ccw 1.2s linear infinite;
}
@keyframes valve-spin-cw {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes valve-spin-ccw {
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
}

/* wizard */
.wizard-dot {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e4e6ef;
  color: #7e8299;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
  font-size: 12px;
}
.wizard-dot-active {
  background: #3699ff;
  color: #fff;
}
.wizard-dot-done {
  background: #1bc5bd;
  color: #fff;
}
.jog-btn {
  min-width: 140px;
}
</style>
